import React, { useEffect, useState } from "react";
import { getEmployeeProfitability } from "../../../services/productionProfitabilityService";
import ScoreBadge from "./ScoreBadge";
import AlertList from "./AlertList";
import KpiCard from "./KpiCard";
import RecommendationCard from "./RecommendationCard";
import ServiceBreakdownTable from "./ServiceBreakdownTable";

/**
 * Formateurs null-safe
 */
function money(v) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(v);
}

function pct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(1)}%`;
}

function num(v, digits = 1) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(digits);
}

/**
 * Panel de rentabilité employé
 * Affiche score, KPIs, alertes, recommandation, breakdown par service
 */
export default function EmployeeProfitabilityPanel({ employeeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      if (!employeeId) return;
      
      try {
        setLoading(true);
        setError("");
        const res = await getEmployeeProfitability(employeeId);
        setData(res);
      } catch (err) {
        console.error("Erreur chargement rentabilité employé:", err);
        setError("Impossible de charger les données de rentabilité.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [employeeId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="text-center text-gray-500">
          <div className="animate-pulse">📊 Chargement des données de rentabilité...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="text-red-700">⚠️ {error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { totals, score, alerts, recommendation, service_breakdown } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-2xl font-bold text-gray-900">💼 Rentabilité Employé</div>
            <div className="text-sm text-gray-500 mt-1">{data.employee_name}</div>
          </div>
          <ScoreBadge score={score.letter} numeric={score.numeric} />
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mt-6">
          <KpiCard label="Profit brut" value={money(totals.gross_profit)} />
          <KpiCard label="Profit / heure" value={money(totals.profit_per_hour)} />
          <KpiCard label="Marge" value={pct(totals.margin_percent)} />
          <KpiCard label="Heures réelles" value={num(totals.hours_worked, 1)} />
          <KpiCard label="Écart temps" value={pct(totals.time_variance_percent)} />
          <KpiCard label="Stops complétés" value={totals.stops_completed} />
        </div>
      </div>

      {/* Recommandation */}
      <RecommendationCard recommendation={recommendation} />

      {/* Alertes */}
      {alerts && alerts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="text-lg font-semibold mb-4">⚠️ Alertes</div>
          <AlertList alerts={alerts} />
        </div>
      )}

      {/* Breakdown par service */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="text-lg font-semibold mb-4">📊 Performance par type de service</div>
        <ServiceBreakdownTable rows={service_breakdown} />
      </div>

      {/* Stats supplémentaires */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="text-lg font-semibold mb-4">📊 Statistiques détaillées</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">Routes complétées</div>
            <div className="text-xl font-bold">{totals.routes_completed}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Clients servis</div>
            <div className="text-xl font-bold">{totals.clients_served_count}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Revenu / heure</div>
            <div className="text-xl font-bold">{money(totals.revenue_per_hour)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Revenu total</div>
            <div className="text-xl font-bold">{money(totals.revenue_total)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
